import cornerstoneWADOImageLoader from 'cornerstone-wado-image-loader';

/**
 * Custom replacement for `cornerstoneWADOImageLoader.wadouri.loadImage`, registered for the
 * internal `dicomfile-raw:` sub-scheme (see cornerstone-setup.js). dicomfile-loader.ts delegates
 * first-load byte fetching + decode here.
 *
 * Why this exists — the multiframe load-speed footgun:
 * WADO's frame extractors return a frame's pixelData as a *view onto the entire P10 file's
 * ArrayBuffer*, not a standalone buffer:
 *   - getUncompressedImageFrame: `new Uint8Array(dataSet.byteArray.buffer, frameOffset, len)`
 *   - getEncapsulatedImageFrame (single-fragment-per-frame path): a `sharedCopy` view
 * When that view is posted to the decode web worker, postMessage's structured clone copies the
 * WHOLE backing ArrayBuffer (the entire file), not just the frame's bytes. For a large
 * uncompressed multiframe (e.g. a US cine) that means copying the whole multi-hundred-MB file
 * once *per frame* — O(frames × fileSize) of pure memcpy — which dwarfs the (skipped) decode cost
 * and makes raw files load far slower than their lossless-compressed twins, whose backing buffer
 * is the much smaller compressed file.
 *
 * Fix: copy each frame into a tight, frame-sized buffer (`.slice()`) before decode, then *transfer*
 * it to the worker (`transferPixelData`) for a zero-copy handoff. The worker now only ever receives
 * one frame's worth of bytes. This mirrors the rest of wadouri.loadImage's behavior (dataSet cache,
 * shared cache key, image.data) so the surrounding flow in loadImages.ts is unaffected.
 */

const wadoLoader = cornerstoneWADOImageLoader as any;
const { createImage } = wadoLoader;
const {
  dataSetCacheManager,
  parseImageId,
  getEncapsulatedImageFrame,
  getUncompressedImageFrame,
} = wadoLoader.wadouri;

/**
 * Replicates wadouri.getPixelData's encapsulated-vs-native selection, but returns a tight,
 * frame-owned Uint8Array instead of a view onto the whole-file buffer.
 */
function getTightImageFrame(dataSet: any, frame: number): Uint8Array | null {
  const pixelDataElement = dataSet.elements.x7fe00010 || dataSet.elements.x7fe00008;
  if (!pixelDataElement) return null;

  const raw: Uint8Array = pixelDataElement.encapsulatedPixelData
    ? getEncapsulatedImageFrame(dataSet, frame)
    : getUncompressedImageFrame(dataSet, frame);

  // If `raw` is a view onto a larger (shared) buffer — the whole-file case for uncompressed and
  // single-fragment-encapsulated frames — copy it into a buffer of exactly this frame's length so
  // the worker handoff moves one frame, not the entire file. An already-tight, frame-owned buffer
  // (multi-fragment-encapsulated path, which allocs its own buffer) is transferred as-is, with no
  // redundant copy.
  //
  // The explicit check against the dataSet's own buffer matters because the parsed file now
  // outlives a single frame decode (instance-source keeps it hot). A single-frame file whose
  // pixel data happens to span its whole buffer would look "tight" here, and transferring it
  // would detach the file itself — corrupting every later frame rather than just this one.
  const isTight =
    raw.byteOffset === 0 &&
    raw.byteLength === raw.buffer.byteLength &&
    raw.buffer !== dataSet.byteArray.buffer;
  return isTight ? raw : raw.slice();
}

export default function dicomfileRawLoader(imageId: string, options: any = {}) {
  const parsed = parseImageId(imageId);
  const frame = parsed.frame ?? 0; // single-frame imageIds have no ?frame= suffix
  const opts = Object.assign({}, options);
  // Safe to transfer (vs structured-clone) because each frame now owns its buffer and we never
  // read the input pixelData again after handing it to the worker.
  opts.transferPixelData = true;

  const promise = (async () => {
    // dicomfile-loader is the only caller and always pins the dataSet first (see
    // instance-source). Asserting rather than loading here keeps dataSetCacheManager's refcount
    // owned entirely by instance-source — exactly one load and one unload per parsed file.
    if (!dataSetCacheManager.isLoaded(parsed.url)) {
      throw new Error(
        `dicomfile-raw: dataSet "${parsed.url}" was not acquired before decode (${imageId})`
      );
    }
    const dataSet = dataSetCacheManager.get(parsed.url);

    const pixelData = getTightImageFrame(dataSet, frame);
    const transferSyntax = dataSet.string('x00020010');
    const image = await createImage(imageId, pixelData, transferSyntax, opts);

    // Deliberately NOT setting image.data = dataSet (which wadouri.loadImage does): a cached frame
    // holding the dataSet pins the entire source file, unaccounted for by image.sizeInBytes.
    image.sharedCacheKey = parsed.url;
    return image;
  })();

  return { promise };
}
